import React, { useMemo, useState } from 'react';
import { mockCandidates } from '../utils/mockData';
import { Logo } from '../components/Logo';
import { DrilldownModal } from '../components/DrilldownModal';
import { Candidate } from '../types/hr.types';
import { Calendar, Filter } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { Users, UserCheck, UserX, FileText, TrendingUp, TrendingDown, DollarSign, Activity } from 'lucide-react';
const COLORS = ['#2563EB', '#3B82F6', '#60A5FA', '#93C5FD', '#BFDBFE'];
type Timeframe = 'all-time' | 'last-7' | 'last-14' | 'last-30' | 'last-60' | 'last-90' | 'this-month' | 'last-month' | 'custom';
const TIMEFRAME_OPTIONS: {
  id: Timeframe;
  label: string;
}[] = [{
  id: 'all-time',
  label: 'All Time'
}, {
  id: 'last-7',
  label: 'Last 7 days'
}, {
  id: 'last-14',
  label: 'Last 14 days'
}, {
  id: 'last-30',
  label: 'Last 30 days'
}, {
  id: 'last-60',
  label: 'Last 60 days'
}, {
  id: 'last-90',
  label: 'Last 90 days'
}, {
  id: 'this-month',
  label: 'This Month'
}, {
  id: 'last-month',
  label: 'Last Month'
}, {
  id: 'custom',
  label: 'Custom Range'
}];
function getTimeframeBounds(tf: Timeframe, customStart: string, customEnd: string): {
  start: Date | null;
  end: Date | null;
} {
  const now = new Date();
  const rollingDays: Partial<Record<Timeframe, number>> = {
    'last-7': 7,
    'last-14': 14,
    'last-30': 30,
    'last-60': 60,
    'last-90': 90
  };
  if (rollingDays[tf]) {
    const start = new Date(now);
    start.setDate(now.getDate() - rollingDays[tf]!);
    start.setHours(0, 0, 0, 0);
    return {
      start,
      end: now
    };
  }
  if (tf === 'this-month') {
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
    return {
      start,
      end
    };
  }
  if (tf === 'last-month') {
    const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const end = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
    return {
      start,
      end
    };
  }
  if (tf === 'custom') {
    const start = customStart ? new Date(customStart) : null;
    const end = customEnd ? new Date(`${customEnd}T23:59:59`) : null;
    return {
      start,
      end
    };
  }
  return {
    start: null,
    end: null
  };
}
type ActivityAccent = 'orange' | 'purple' | 'blue' | 'indigo' | 'amber' | 'green';
const ACCENT_CLASSES: Record<ActivityAccent, {
  bg: string;
  text: string;
  dot: string;
}> = {
  orange: {
    bg: 'bg-orange-50',
    text: 'text-orange-700',
    dot: 'bg-orange-500'
  },
  purple: {
    bg: 'bg-purple-50',
    text: 'text-purple-700',
    dot: 'bg-purple-500'
  },
  blue: {
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    dot: 'bg-blue-500'
  },
  indigo: {
    bg: 'bg-indigo-50',
    text: 'text-indigo-700',
    dot: 'bg-indigo-500'
  },
  amber: {
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    dot: 'bg-amber-500'
  },
  green: {
    bg: 'bg-green-50',
    text: 'text-green-700',
    dot: 'bg-green-500'
  }
};
interface ActivityItem {
  label: string;
  value: number | string;
  muted?: boolean;
  warn?: boolean;
  onClick?: () => void;
}
function ActivityGroup({
  label,
  accent,
  items
}: {
  label: string;
  accent: ActivityAccent;
  items: ActivityItem[];
}) {
  const cls = ACCENT_CLASSES[accent];
  return <div className="rounded-xl border border-gray-100 p-4 bg-gray-50/50" data-id="element-1981">
      <div className="flex items-center gap-2 mb-3" data-id="element-1982">
        <span className={`h-2 w-2 rounded-full ${cls.dot}`} data-id="element-1983"></span>
        <h3 className={`text-[11px] font-semibold uppercase tracking-wider ${cls.text}`} data-id="element-1984">
          {label}
        </h3>
      </div>
      <div className="space-y-2.5" data-id="element-1985">
        {items.map(item => {
        const interactive = !!item.onClick;
        const Tag = interactive ? 'button' : 'div';
        return <Tag key={item.label} onClick={item.onClick} className={`w-full flex items-baseline justify-between gap-3 text-left ${interactive ? 'hover:bg-white -mx-2 px-2 py-1 rounded-md transition-colors' : ''}`} data-id="element-1986">
              <span className="text-xs text-gray-600" data-id="element-1987">{item.label}</span>
              <span className={`text-xl font-bold tabular-nums ${item.warn ? 'text-red-600' : item.muted ? 'text-gray-500' : 'text-gray-900'}`} data-id="element-1988">
                {item.value}
              </span>
            </Tag>;
      })}
      </div>
    </div>;
}
export function Reports() {
  const [timeframe, setTimeframe] = useState<Timeframe>('all-time');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [recruiterFilter, setRecruiterFilter] = useState<string>('all');
  const [stageFilter, setStageFilter] = useState<string>('all');
  const [positionFilter, setPositionFilter] = useState<string>('all');
  const [drilldown, setDrilldown] = useState<{
    title: string;
    subtitle?: string;
    candidates: Candidate[];
  } | null>(null);
  const openDrilldown = (title: string, candidates: Candidate[], subtitle?: string) => setDrilldown({
    title,
    candidates,
    subtitle
  });
  // Only one recruiter is active for now — keep the dropdown scoped to her.
  const recruiterOptions = useMemo(() => ['Mirna Hermiz'], []);
  const positionOptions = useMemo(() => {
    const set = new Set<string>();
    mockCandidates.forEach(c => {
      if (c.role) set.add(c.role);
    });
    return Array.from(set).sort();
  }, []);
  const stageOptions = ['Phone Screen', 'In Person Interview', 'Offer Sent', 'Waiting on Response', 'Offer Accepted', 'Pre-boarding', 'Ready to Start', 'Offer Declined', 'Follow Up Later', 'Hired Elsewhere', 'Declined'];
  // Apply timeframe + recruiter filter once; everything downstream uses this.
  // Date filtering uses phoneInterviewDate (when the candidate entered the
  // pipeline) — the most universal date on a candidate record.
  const filteredCandidates = useMemo(() => {
    const {
      start,
      end
    } = getTimeframeBounds(timeframe, customStart, customEnd);
    return mockCandidates.filter(c => {
      if (recruiterFilter !== 'all') {
        const r = c.recruiter || c.interviewedBy;
        if (r !== recruiterFilter) return false;
      }
      if (stageFilter !== 'all' && c.status !== stageFilter) return false;
      if (positionFilter !== 'all' && c.role !== positionFilter) return false;
      if (start || end) {
        const ref = c.phoneInterviewDate ? new Date(c.phoneInterviewDate) : null;
        if (!ref || isNaN(ref.getTime())) return false;
        if (start && ref < start) return false;
        if (end && ref > end) return false;
      }
      return true;
    });
  }, [timeframe, customStart, customEnd, recruiterFilter, stageFilter, positionFilter]);
  const stats = useMemo(() => {
    const total = filteredCandidates.length;
    const interviewed = filteredCandidates.filter(c => c.status === 'In Person Interview' || c.status === 'Phone Screen').length;
    const hired = filteredCandidates.filter(c => c.status === 'Offer Accepted' || c.status === 'Pre-boarding' || c.status === 'Ready to Start').length;
    const rejected = filteredCandidates.filter(c => c.status === 'Declined').length;
    const offersSent = filteredCandidates.filter(c => c.status === 'Offer Sent' || c.status === 'Waiting on Response' || c.status === 'Offer Accepted' || c.status === 'Offer Declined').length;
    const declinedCandidates = filteredCandidates.filter(c => c.status === 'Offer Declined');
    const offersDeclined = declinedCandidates.length;
    const declinedSalaries = declinedCandidates.map(c => c.offerAmount).filter((a): a is number => typeof a === 'number');
    const avgDeclinedSalary = declinedSalaries.length > 0 ? Math.round(declinedSalaries.reduce((a, b) => a + b, 0) / declinedSalaries.length) : null;
    const maxDeclinedSalary = declinedSalaries.length > 0 ? Math.max(...declinedSalaries) : null;
    return {
      total,
      interviewed,
      hired,
      rejected,
      offersSent,
      offersDeclined,
      avgDeclinedSalary,
      maxDeclinedSalary,
      declineRate: offersSent > 0 ? Math.round(offersDeclined / offersSent * 100) : 0,
      conversionRate: total > 0 ? Math.round(hired / total * 100) : 0,
      offerAcceptanceRate: offersSent > 0 ? Math.round(hired / offersSent * 100) : 0
    };
  }, [filteredCandidates]);
  const pipelineData = useMemo(() => {
    const stages = ['Phone Screen', 'In Person Interview', 'Offer Sent', 'Offer Accepted', 'Hired'];
    return stages.map(stage => ({
      name: stage,
      count: filteredCandidates.filter(c => {
        if (stage === 'Hired') {
          return c.status === 'Pre-boarding' || c.status === 'Ready to Start' || c.status === 'Offer Accepted';
        }
        return c.status === stage;
      }).length
    }));
  }, [filteredCandidates]);
  const recruiterData = useMemo(() => {
    const recruiters = Array.from(new Set(filteredCandidates.map(c => c.recruiter || c.interviewedBy)));
    return recruiters.map(recruiter => ({
      name: recruiter,
      candidates: filteredCandidates.filter(c => (c.recruiter || c.interviewedBy) === recruiter).length,
      hired: filteredCandidates.filter(c => (c.recruiter || c.interviewedBy) === recruiter && (c.status === 'Offer Accepted' || c.status === 'Pre-boarding' || c.status === 'Ready to Start')).length
    }));
  }, [filteredCandidates]);
  const roleData = useMemo(() => {
    const roles = Array.from(new Set(filteredCandidates.map(c => c.role)));
    return roles.map(role => ({
      name: role,
      value: filteredCandidates.filter(c => c.role === role).length
    }));
  }, [filteredCandidates]);
  const declinedRolesData = useMemo(() => {
    const declined = filteredCandidates.filter(c => c.status === 'Offer Declined');
    const counts: Record<string, number> = {};
    declined.forEach(c => {
      counts[c.role] = (counts[c.role] || 0) + 1;
    });
    return Object.entries(counts).map(([name, count]) => ({
      name,
      count
    })).sort((a, b) => b.count - a.count).slice(0, 6);
  }, [filteredCandidates]);
  const declinedSalaryData = useMemo(() => {
    const declined = filteredCandidates.filter(c => c.status === 'Offer Declined' && typeof c.offerAmount === 'number');
    const buckets = {
      '<$50k': 0,
      '$50–60k': 0,
      '$60–70k': 0,
      '$70–80k': 0,
      '$80–90k': 0,
      '$90k+': 0
    };
    declined.forEach(c => {
      const amt = c.offerAmount!;
      if (amt < 50000) buckets['<$50k']++;else if (amt < 60000) buckets['$50–60k']++;else if (amt < 70000) buckets['$60–70k']++;else if (amt < 80000) buckets['$70–80k']++;else if (amt < 90000) buckets['$80–90k']++;else buckets['$90k+']++;
    });
    return Object.entries(buckets).map(([name, count]) => ({
      name,
      count
    }));
  }, [filteredCandidates]);
  const formatCompactUSD = (val: number | null) => {
    if (val === null) return '—';
    return `${Math.round(val / 1000)}k`;
  };
  // Granular activity metrics — each action that can be taken in the workflow,
  // counted using the most relevant date field, scoped to the active timeframe
  // and recruiter/position filters (but ignoring the stage filter so the
  // metrics reflect cumulative activity regardless of where a candidate ended).
  const activityMetrics = useMemo(() => {
    const {
      start,
      end
    } = getTimeframeBounds(timeframe, customStart, customEnd);
    const inRange = (dateStr?: string) => {
      if (!dateStr) return false;
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return false;
      if (start && d < start) return false;
      if (end && d > end) return false;
      return true;
    };
    const matchesFilters = (c: (typeof mockCandidates)[number]) => {
      if (recruiterFilter !== 'all') {
        const r = c.recruiter || c.interviewedBy;
        if (r !== recruiterFilter) return false;
      }
      if (positionFilter !== 'all' && c.role !== positionFilter) return false;
      return true;
    };
    const pool = mockCandidates.filter(matchesFilters);
    const phoneScreensScheduled = pool.filter(c => inRange(c.phoneInterviewDate)).length;
    // "Completed" = candidate has progressed past the Phone Screen stage in
    // the active timeframe (phone interview happened in range AND status is
    // anything past Phone Screen).
    const PHONE_DONE = new Set(['In Person Interview', 'Offer Sent', 'Waiting on Response', 'Offer Accepted', 'Pre-boarding', 'Ready to Start', 'Offer Declined', 'Hired Elsewhere', 'Follow Up Later', 'Declined']);
    const phoneScreensCompleted = pool.filter(c => inRange(c.phoneInterviewDate) && PHONE_DONE.has(c.status)).length;
    const phoneScreensNoShow = pool.filter(c => inRange(c.phoneInterviewDate) && (c.status === 'Hired Elsewhere' || c.status === 'Declined') && !c.inPersonInterviewDate).length;
    const interviewsScheduled = pool.filter(c => inRange(c.inPersonInterviewDate)).length;
    const INTERVIEW_DONE = new Set(['Offer Sent', 'Waiting on Response', 'Offer Accepted', 'Pre-boarding', 'Ready to Start', 'Offer Declined', 'Hired Elsewhere', 'Declined']);
    const interviewsCompleted = pool.filter(c => inRange(c.inPersonInterviewDate) && INTERVIEW_DONE.has(c.status)).length;
    const offersSent = pool.filter(c => inRange(c.offerSentDate)).length;
    const offersAccepted = pool.filter(c => inRange(c.offerDecisionDate) && (c.status === 'Offer Accepted' || c.status === 'Pre-boarding' || c.status === 'Ready to Start')).length;
    const offersDeclined = pool.filter(c => inRange(c.offerDecisionDate) && c.status === 'Offer Declined').length;
    const followUpsScheduled = pool.filter(c => inRange(c.followUpDate)).length;
    // Completed = a follow-up date was set in range and the candidate has
    // since moved out of the Follow Up Later bucket.
    const followUpsCompleted = pool.filter(c => inRange(c.followUpDate) && c.status !== 'Follow Up Later' && c.status !== 'Hired Elsewhere').length;
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    const followUpsOverdue = pool.filter(c => c.status === 'Follow Up Later' && c.followUpDate && new Date(c.followUpDate) < todayStart).length;
    const candidatesAdded = pool.filter(c => inRange(c.phoneInterviewDate)).length;
    const hires = pool.filter(c => inRange(c.offerDecisionDate) && (c.status === 'Offer Accepted' || c.status === 'Pre-boarding' || c.status === 'Ready to Start')).length;
    const onboarded = pool.filter(c => c.status === 'Ready to Start').length;
    const showUpRate = phoneScreensScheduled > 0 ? Math.round(phoneScreensCompleted / phoneScreensScheduled * 100) : 0;
    const interviewShowUpRate = interviewsScheduled > 0 ? Math.round(interviewsCompleted / interviewsScheduled * 100) : 0;
    // Build per-metric candidate lists too — used by the drilldown modal.
    const lists = {
      candidatesAdded: pool.filter(c => inRange(c.phoneInterviewDate)),
      phoneScreensScheduled: pool.filter(c => inRange(c.phoneInterviewDate)),
      phoneScreensCompleted: pool.filter(c => inRange(c.phoneInterviewDate) && PHONE_DONE.has(c.status)),
      interviewsScheduled: pool.filter(c => inRange(c.inPersonInterviewDate)),
      interviewsCompleted: pool.filter(c => inRange(c.inPersonInterviewDate) && INTERVIEW_DONE.has(c.status)),
      offersSent: pool.filter(c => inRange(c.offerSentDate)),
      offersAccepted: pool.filter(c => inRange(c.offerDecisionDate) && (c.status === 'Offer Accepted' || c.status === 'Pre-boarding' || c.status === 'Ready to Start')),
      offersDeclined: pool.filter(c => inRange(c.offerDecisionDate) && c.status === 'Offer Declined'),
      followUpsScheduled: pool.filter(c => inRange(c.followUpDate)),
      followUpsCompleted: pool.filter(c => inRange(c.followUpDate) && c.status !== 'Follow Up Later' && c.status !== 'Hired Elsewhere'),
      followUpsOverdue: pool.filter(c => c.status === 'Follow Up Later' && c.followUpDate && new Date(c.followUpDate) < todayStart),
      hires: pool.filter(c => inRange(c.offerDecisionDate) && (c.status === 'Offer Accepted' || c.status === 'Pre-boarding' || c.status === 'Ready to Start')),
      onboarded: pool.filter(c => c.status === 'Ready to Start')
    };
    return {
      candidatesAdded,
      phoneScreensScheduled,
      phoneScreensCompleted,
      phoneScreensNoShow,
      showUpRate,
      interviewsScheduled,
      interviewsCompleted,
      interviewShowUpRate,
      offersSent,
      offersAccepted,
      offersDeclined,
      followUpsScheduled,
      followUpsCompleted,
      followUpsOverdue,
      hires,
      onboarded,
      lists
    };
  }, [timeframe, customStart, customEnd, recruiterFilter, positionFilter]);
  return <div className="min-h-screen bg-gray-50 p-6 md:p-8 font-sans text-gray-900 overflow-x-hidden" data-id="element-1989">
      <div className="max-w-[1600px] mx-auto space-y-6 min-w-0" data-id="element-1990">
        <header className="relative overflow-hidden rounded-2xl bg-[#1B2B6B] p-8 md:p-10 shadow-xl shadow-[#1B2B6B]/30" data-id="element-1991">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(255,255,255,0.15)_0%,_transparent_60%)]" data-id="element-1992"></div>
          <div className="absolute bottom-0 right-0 w-1/2 h-1/2 bg-gradient-to-tl from-white/10 to-transparent" data-id="element-1993"></div>
          <div className="relative flex items-center gap-8" data-id="element-1994">
            <div className="flex-shrink-0 bg-white px-3 py-2 rounded-xl shadow-sm" data-id="element-1995">
              <Logo className="h-10 md:h-12 w-auto" data-id="element-1996" />
            </div>
            <div className="flex-1 min-w-0" data-id="element-1997">
              <h1 className="text-2xl md:text-3xl font-bold text-white tracking-tight drop-shadow-sm" data-id="element-1998">
                Recruiting Reports
              </h1>
              <p className="text-orange-100 mt-1 text-sm md:text-base" data-id="element-1999">
                Track pipeline health, conversion metrics, and recruiter
                activity.
              </p>
            </div>
          </div>
        </header>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 flex flex-col md:flex-row md:items-center gap-4" data-id="element-2000">
          <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 flex-shrink-0" data-id="element-2001">
            <Filter className="h-4 w-4 text-gray-400" data-id="element-2002" />
            Filters
          </div>
          <div className="flex flex-wrap items-center gap-2 flex-1" data-id="element-2003">
            {/* Timeframe pills */}
            <div className="inline-flex rounded-lg bg-gray-100 p-1" data-id="element-2004">
              {TIMEFRAME_OPTIONS.map(opt => {
              const active = timeframe === opt.id;
              return <button key={opt.id} onClick={() => setTimeframe(opt.id)} className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${active ? 'bg-white text-orange-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-2005">
                    {opt.label}
                  </button>;
            })}
            </div>

            {/* Custom date range — only when Custom is selected */}
            {timeframe === 'custom' && <div className="flex items-center gap-2 px-2" data-id="element-2006">
                <Calendar className="h-4 w-4 text-gray-400" data-id="element-2007" />
                <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)} className="px-2 py-1.5 border border-gray-200 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-orange-500" data-id="element-2008" />
                <span className="text-gray-400 text-xs" data-id="element-2009">→</span>
                <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)} className="px-2 py-1.5 border border-gray-200 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-orange-500" data-id="element-2010" />
              </div>}

            {/* Recruiter select */}
            <div className="flex items-center gap-2 ml-auto" data-id="element-2011">
              <label className="text-xs font-semibold text-gray-600" data-id="element-2012">
                Recruiter
              </label>
              <select value={recruiterFilter} onChange={e => setRecruiterFilter(e.target.value)} className="px-2.5 py-1.5 border border-gray-200 rounded-md text-xs bg-white focus:outline-none focus:ring-2 focus:ring-orange-500" data-id="element-2013">
                <option value="all" data-id="element-2014">All recruiters</option>
                {recruiterOptions.map(r => <option key={r} value={r} data-id="element-2015">
                    {r}
                  </option>)}
              </select>
            </div>

            {/* Clear filters */}
            {(timeframe !== 'all-time' || recruiterFilter !== 'all') && <button onClick={() => {
            setTimeframe('all-time');
            setCustomStart('');
            setCustomEnd('');
            setRecruiterFilter('all');
          }} className="text-xs font-semibold text-gray-500 hover:text-orange-700 px-2 py-1.5" data-id="element-2016">
                Clear
              </button>}
          </div>

          {/* Result count */}
          <div className="text-xs text-gray-500 whitespace-nowrap" data-id="element-2017">
            <span className="font-semibold text-gray-900" data-id="element-2018">
              {filteredCandidates.length}
            </span>{' '}
            candidate{filteredCandidates.length === 1 ? '' : 's'} matched
          </div>
        </div>

        {/* Top Stats — click any card to drill into the underlying candidates */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-2019">
          <button onClick={() => openDrilldown('Total Candidates', filteredCandidates, 'Every candidate matching the current filters.')} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 text-left hover:border-orange-200 hover:shadow-md transition-all" data-id="element-2020">
            <div className="flex items-center justify-between" data-id="element-2021">
              <div data-id="element-2022">
                <p className="text-sm font-medium text-gray-500" data-id="element-2023">
                  Total Candidates
                </p>
                <p className="text-3xl font-bold text-gray-900 mt-1" data-id="element-2024">
                  {stats.total}
                </p>
              </div>
              <div className="h-12 w-12 bg-orange-50 rounded-full flex items-center justify-center" data-id="element-2025">
                <Users className="h-6 w-6 text-orange-600" data-id="element-2026" />
              </div>
            </div>
          </button>
          <button onClick={() => openDrilldown('Hires', filteredCandidates.filter(c => c.status === 'Offer Accepted' || c.status === 'Pre-boarding' || c.status === 'Ready to Start'), 'Candidates who accepted their offer.')} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 text-left hover:border-green-200 hover:shadow-md transition-all" data-id="element-2027">
            <div className="flex items-center justify-between" data-id="element-2028">
              <div data-id="element-2029">
                <p className="text-sm font-medium text-gray-500" data-id="element-2030">Hires</p>
                <p className="text-3xl font-bold text-gray-900 mt-1" data-id="element-2031">
                  {stats.hired}
                </p>
              </div>
              <div className="h-12 w-12 bg-green-50 rounded-full flex items-center justify-center" data-id="element-2032">
                <UserCheck className="h-6 w-6 text-green-600" data-id="element-2033" />
              </div>
            </div>
          </button>
          <button onClick={() => openDrilldown('Offers Sent', filteredCandidates.filter(c => c.status === 'Offer Sent' || c.status === 'Waiting on Response' || c.status === 'Offer Accepted' || c.status === 'Offer Declined'), 'Candidates who received an offer letter.')} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 text-left hover:border-blue-200 hover:shadow-md transition-all" data-id="element-2034">
            <div className="flex items-center justify-between" data-id="element-2035">
              <div data-id="element-2036">
                <p className="text-sm font-medium text-gray-500" data-id="element-2037">Offers Sent</p>
                <p className="text-3xl font-bold text-gray-900 mt-1" data-id="element-2038">
                  {stats.offersSent}
                </p>
              </div>
              <div className="h-12 w-12 bg-blue-50 rounded-full flex items-center justify-center" data-id="element-2039">
                <FileText className="h-6 w-6 text-blue-600" data-id="element-2040" />
              </div>
            </div>
          </button>
          <button onClick={() => openDrilldown('Offer Acceptance', filteredCandidates.filter(c => c.status === 'Offer Accepted' || c.status === 'Pre-boarding' || c.status === 'Ready to Start'), `${stats.offerAcceptanceRate}% of sent offers were accepted.`)} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 text-left hover:border-purple-200 hover:shadow-md transition-all" data-id="element-2041">
            <div className="flex items-center justify-between" data-id="element-2042">
              <div data-id="element-2043">
                <p className="text-sm font-medium text-gray-500" data-id="element-2044">
                  Offer Acceptance
                </p>
                <p className="text-3xl font-bold text-gray-900 mt-1" data-id="element-2045">
                  {stats.offerAcceptanceRate}%
                </p>
              </div>
              <div className="h-12 w-12 bg-purple-50 rounded-full flex items-center justify-center" data-id="element-2046">
                <TrendingUp className="h-6 w-6 text-purple-600" data-id="element-2047" />
              </div>
            </div>
          </button>
        </div>

        {/* Activity Metrics — granular per-action counts */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6" data-id="element-2048">
          <div className="mb-5" data-id="element-2049">
            <h2 className="text-lg font-bold text-gray-900" data-id="element-2050">
              Activity Metrics
            </h2>
            <p className="text-sm text-gray-500 mt-0.5" data-id="element-2051">
              Every action taken in the recruiting workflow, scoped to the
              active timeframe and filters.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4" data-id="element-2052">
            <ActivityGroup label="Intake" accent="orange" items={[{
            label: 'Candidates Added',
            value: activityMetrics.candidatesAdded,
            onClick: () => openDrilldown('Candidates Added', activityMetrics.lists.candidatesAdded, 'Candidates added to the pipeline in the current timeframe.')
          }]} data-id="element-2053" />
            <ActivityGroup label="Phone Screen" accent="purple" items={[{
            label: 'Scheduled',
            value: activityMetrics.phoneScreensScheduled,
            onClick: () => openDrilldown('Phone Screens Scheduled', activityMetrics.lists.phoneScreensScheduled, 'Candidates with a phone screen scheduled in the current timeframe.')
          }, {
            label: 'Completed',
            value: activityMetrics.phoneScreensCompleted,
            onClick: () => openDrilldown('Phone Screens Completed', activityMetrics.lists.phoneScreensCompleted, 'Candidates who progressed past Phone Screen.')
          }, {
            label: 'Show-up Rate',
            value: `${activityMetrics.showUpRate}%`,
            muted: true
          }]} data-id="element-2054" />
            <ActivityGroup label="In-Person Interview" accent="blue" items={[{
            label: 'Scheduled',
            value: activityMetrics.interviewsScheduled,
            onClick: () => openDrilldown('Interviews Scheduled', activityMetrics.lists.interviewsScheduled, 'Candidates with an in-person interview scheduled.')
          }, {
            label: 'Completed',
            value: activityMetrics.interviewsCompleted,
            onClick: () => openDrilldown('Interviews Completed', activityMetrics.lists.interviewsCompleted, 'Candidates who progressed past the in-person interview.')
          }, {
            label: 'Show-up Rate',
            value: `${activityMetrics.interviewShowUpRate}%`,
            muted: true
          }]} data-id="element-2055" />
            <ActivityGroup label="Offers" accent="indigo" items={[{
            label: 'Sent',
            value: activityMetrics.offersSent,
            onClick: () => openDrilldown('Offers Sent', activityMetrics.lists.offersSent, 'Candidates who received an offer letter.')
          }, {
            label: 'Accepted',
            value: activityMetrics.offersAccepted,
            onClick: () => openDrilldown('Offers Accepted', activityMetrics.lists.offersAccepted, 'Candidates who accepted their offer.')
          }, {
            label: 'Declined',
            value: activityMetrics.offersDeclined,
            onClick: () => openDrilldown('Offers Declined', activityMetrics.lists.offersDeclined, 'Candidates who declined their offer.')
          }]} data-id="element-2056" />
            <ActivityGroup label="Follow-Ups" accent="amber" items={[{
            label: 'Scheduled',
            value: activityMetrics.followUpsScheduled,
            onClick: () => openDrilldown('Follow-Ups Scheduled', activityMetrics.lists.followUpsScheduled, 'Candidates with a follow-up scheduled in the current timeframe.')
          }, {
            label: 'Completed',
            value: activityMetrics.followUpsCompleted,
            onClick: () => openDrilldown('Follow-Ups Completed', activityMetrics.lists.followUpsCompleted, 'Candidates who moved out of Follow Up Later after a scheduled date.')
          }, {
            label: 'Overdue',
            value: activityMetrics.followUpsOverdue,
            warn: activityMetrics.followUpsOverdue > 0,
            onClick: () => openDrilldown('Overdue Follow-Ups', activityMetrics.lists.followUpsOverdue, 'Candidates whose follow-up date has passed without action.')
          }]} data-id="element-2057" />
            <ActivityGroup label="Outcomes" accent="green" items={[{
            label: 'Hires',
            value: activityMetrics.hires,
            onClick: () => openDrilldown('Hires', activityMetrics.lists.hires, 'Candidates who became hires in the current timeframe.')
          }, {
            label: 'Onboarded',
            value: activityMetrics.onboarded,
            onClick: () => openDrilldown('Onboarded', activityMetrics.lists.onboarded, 'Candidates who completed onboarding and are Ready to Start.')
          }]} data-id="element-2058" />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-2059">
          {/* Pipeline Chart */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 min-w-0 overflow-hidden" data-id="element-2060">
            <h3 className="text-lg font-semibold text-gray-900 mb-6" data-id="element-2061">
              Pipeline Stages
            </h3>
            <div className="h-80 w-full" data-id="element-2062">
              <ResponsiveContainer width="100%" height="100%" data-id="element-2063">
                <BarChart data={pipelineData} layout="vertical" margin={{
                top: 5,
                right: 20,
                left: 5,
                bottom: 5
              }} data-id="element-2064">
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} data-id="element-2065" />
                  <XAxis type="number" allowDecimals={false} tick={{
                  fontSize: 12
                }} data-id="element-2066" />
                  <YAxis dataKey="name" type="category" width={130} tick={{
                  fontSize: 12
                }} data-id="element-2067" />
                  <Tooltip data-id="element-2068" />
                  <Bar dataKey="count" fill="#2563EB" radius={[0, 4, 4, 0]} data-id="element-2069" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Recruiter Activity */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 min-w-0 overflow-hidden" data-id="element-2070">
            <h3 className="text-lg font-semibold text-gray-900 mb-6" data-id="element-2071">
              Recruiter Activity
            </h3>
            <div className="h-80 w-full" data-id="element-2072">
              <ResponsiveContainer width="100%" height="100%" data-id="element-2073">
                <BarChart data={recruiterData} margin={{
                top: 5,
                right: 20,
                left: 0,
                bottom: 5
              }} data-id="element-2074">
                  <CartesianGrid strokeDasharray="3 3" vertical={false} data-id="element-2075" />
                  <XAxis dataKey="name" tick={{
                  fontSize: 12
                }} interval={0} data-id="element-2076" />
                  <YAxis allowDecimals={false} tick={{
                  fontSize: 12
                }} data-id="element-2077" />
                  <Tooltip data-id="element-2078" />
                  <Legend wrapperStyle={{
                  fontSize: 12
                }} data-id="element-2079" />
                  <Bar dataKey="candidates" name="Total Candidates" fill="#93C5FD" radius={[4, 4, 0, 0]} cursor="pointer" onClick={(data: any) => {
                  const recruiter = data?.name as string;
                  openDrilldown(`${recruiter}: all candidates`, filteredCandidates.filter(c => (c.recruiter || c.interviewedBy) === recruiter), `Every candidate worked by ${recruiter}.`);
                }} data-id="element-2080" />
                  <Bar dataKey="hired" name="Hired" fill="#2563EB" radius={[4, 4, 0, 0]} cursor="pointer" onClick={(data: any) => {
                  const recruiter = data?.name as string;
                  openDrilldown(`${recruiter}: hires`, filteredCandidates.filter(c => (c.recruiter || c.interviewedBy) === recruiter && (c.status === 'Offer Accepted' || c.status === 'Pre-boarding' || c.status === 'Ready to Start')), `Hires attributed to ${recruiter}.`);
                }} data-id="element-2081" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Roles Breakdown */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 min-w-0 overflow-hidden" data-id="element-2082">
            <h3 className="text-lg font-semibold text-gray-900 mb-6" data-id="element-2083">
              Candidates by Role
            </h3>
            <div className="h-80 w-full" data-id="element-2084">
              <ResponsiveContainer width="100%" height="100%" data-id="element-2085">
                <PieChart margin={{
                top: 0,
                right: 0,
                left: 0,
                bottom: 0
              }} data-id="element-2086">
                  <Pie data={roleData} cx="50%" cy="50%" innerRadius="40%" outerRadius="70%" paddingAngle={3} dataKey="value" cursor="pointer" onClick={(data: any) => openDrilldown(`Candidates: ${data?.name}`, filteredCandidates.filter(c => c.role === data?.name), `All ${data?.name} candidates in the current view.`)} data-id="element-2087">
                    {roleData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} data-id="element-2088" />)}
                  </Pie>
                  <Tooltip data-id="element-2089" />
                  <Legend wrapperStyle={{
                  fontSize: 12
                }} iconSize={10} data-id="element-2090" />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Conversion Metrics */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 min-w-0 overflow-hidden" data-id="element-2091">
            <h3 className="text-lg font-semibold text-gray-900 mb-6" data-id="element-2092">
              Conversion Metrics
            </h3>
            <div className="space-y-6" data-id="element-2093">
              <div data-id="element-2094">
                <div className="flex justify-between text-sm mb-2" data-id="element-2095">
                  <span className="font-medium text-gray-700" data-id="element-2096">
                    Interview to Offer
                  </span>
                  <span className="text-gray-500" data-id="element-2097">
                    {stats.interviewed > 0 ? Math.round(stats.offersSent / stats.interviewed * 100) : 0}
                    %
                  </span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-2.5" data-id="element-2098">
                  <div className="bg-orange-500 h-2.5 rounded-full" style={{
                  width: `${stats.interviewed > 0 ? Math.round(stats.offersSent / stats.interviewed * 100) : 0}%`
                }} data-id="element-2099"></div>
                </div>
              </div>
              <div data-id="element-2100">
                <div className="flex justify-between text-sm mb-2" data-id="element-2101">
                  <span className="font-medium text-gray-700" data-id="element-2102">
                    Offer to Hire
                  </span>
                  <span className="text-gray-500" data-id="element-2103">
                    {stats.offerAcceptanceRate}%
                  </span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-2.5" data-id="element-2104">
                  <div className="bg-green-500 h-2.5 rounded-full" style={{
                  width: `${stats.offerAcceptanceRate}%`
                }} data-id="element-2105"></div>
                </div>
              </div>
              <div data-id="element-2106">
                <div className="flex justify-between text-sm mb-2" data-id="element-2107">
                  <span className="font-medium text-gray-700" data-id="element-2108">
                    Total Conversion (Lead to Hire)
                  </span>
                  <span className="text-gray-500" data-id="element-2109">{stats.conversionRate}%</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-2.5" data-id="element-2110">
                  <div className="bg-blue-500 h-2.5 rounded-full" style={{
                  width: `${stats.conversionRate}%`
                }} data-id="element-2111"></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* NEW SECTION: Declined Offers Insights */}
        <div className="pt-8 border-t border-gray-200" data-id="element-2112">
          <div className="mb-6" data-id="element-2113">
            <h2 className="text-xl font-bold text-gray-900" data-id="element-2114">Declined Offers</h2>
            <p className="text-sm text-gray-500 mt-1" data-id="element-2115">
              Which positions and salary levels lose candidates.
            </p>
          </div>

          {/* Decline KPIs */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-id="element-2116">
            <button onClick={() => openDrilldown('Offers Declined', filteredCandidates.filter(c => c.status === 'Offer Declined'), 'Candidates who declined an offer.')} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 text-left hover:border-red-200 hover:shadow-md transition-all" data-id="element-2117">
              <div className="flex items-center justify-between" data-id="element-2118">
                <div data-id="element-2119">
                  <p className="text-sm font-medium text-gray-500" data-id="element-2120">
                    Offers Declined
                  </p>
                  <p className="text-3xl font-bold text-gray-900 mt-1" data-id="element-2121">
                    {stats.offersDeclined}
                  </p>
                </div>
                <div className="h-12 w-12 bg-red-50 rounded-full flex items-center justify-center" data-id="element-2122">
                  <UserX className="h-6 w-6 text-red-600" data-id="element-2123" />
                </div>
              </div>
            </button>
            <button onClick={() => openDrilldown('Decline Rate', filteredCandidates.filter(c => c.status === 'Offer Declined'), `${stats.declineRate}% of sent offers were declined.`)} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 text-left hover:border-red-200 hover:shadow-md transition-all" data-id="element-2124">
              <div className="flex items-center justify-between" data-id="element-2125">
                <div data-id="element-2126">
                  <p className="text-sm font-medium text-gray-500" data-id="element-2127">
                    Decline Rate
                  </p>
                  <p className="text-3xl font-bold text-gray-900 mt-1" data-id="element-2128">
                    {stats.declineRate}%
                  </p>
                </div>
                <div className="h-12 w-12 bg-red-50 rounded-full flex items-center justify-center" data-id="element-2129">
                  <TrendingDown className="h-6 w-6 text-red-600" data-id="element-2130" />
                </div>
              </div>
            </button>
            <button onClick={() => openDrilldown('Avg Declined Salary', filteredCandidates.filter(c => c.status === 'Offer Declined' && typeof c.offerAmount === 'number'), `Average offer amount across declined offers: $${formatCompactUSD(stats.avgDeclinedSalary)}.`)} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 text-left hover:border-red-200 hover:shadow-md transition-all" data-id="element-2131">
              <div className="flex items-center justify-between" data-id="element-2132">
                <div data-id="element-2133">
                  <p className="text-sm font-medium text-gray-500" data-id="element-2134">
                    Avg Declined Salary
                  </p>
                  <p className="text-3xl font-bold text-gray-900 mt-1" data-id="element-2135">
                    {formatCompactUSD(stats.avgDeclinedSalary)}
                  </p>
                </div>
                <div className="h-12 w-12 bg-red-50 rounded-full flex items-center justify-center" data-id="element-2136">
                  <DollarSign className="h-6 w-6 text-red-600" data-id="element-2137" />
                </div>
              </div>
            </button>
            <button onClick={() => {
            const declined = filteredCandidates.filter(c => c.status === 'Offer Declined' && typeof c.offerAmount === 'number');
            const max = stats.maxDeclinedSalary;
            openDrilldown('Highest Declined Offer', max === null ? declined : declined.filter(c => c.offerAmount === max), max === null ? 'No declined offers with a salary recorded yet.' : `Candidate(s) who declined the highest offer amount ($${formatCompactUSD(max)}).`);
          }} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 text-left hover:border-red-200 hover:shadow-md transition-all" data-id="element-2138">
              <div className="flex items-center justify-between" data-id="element-2139">
                <div data-id="element-2140">
                  <p className="text-sm font-medium text-gray-500" data-id="element-2141">
                    Highest Declined
                  </p>
                  <p className="text-3xl font-bold text-gray-900 mt-1" data-id="element-2142">
                    {formatCompactUSD(stats.maxDeclinedSalary)}
                  </p>
                </div>
                <div className="h-12 w-12 bg-red-50 rounded-full flex items-center justify-center" data-id="element-2143">
                  <DollarSign className="h-6 w-6 text-red-600" data-id="element-2144" />
                </div>
              </div>
            </button>
          </div>

          {/* Decline Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-2145">
            {/* Most Declined Positions */}
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 min-w-0 overflow-hidden" data-id="element-2146">
              <h3 className="text-lg font-semibold text-gray-900 mb-6" data-id="element-2147">
                Most Declined Positions
              </h3>
              <div className="h-80 w-full" data-id="element-2148">
                {declinedRolesData.length === 0 ? <div className="h-full flex flex-col items-center justify-center text-gray-400" data-id="element-2149">
                    <UserX className="h-12 w-12 mb-3 opacity-20" data-id="element-2150" />
                    <p data-id="element-2151">No declined offers yet</p>
                  </div> : <ResponsiveContainer width="100%" height="100%" data-id="element-2152">
                    <BarChart data={declinedRolesData} layout="vertical" margin={{
                  top: 5,
                  right: 20,
                  left: 5,
                  bottom: 5
                }} data-id="element-2153">
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} data-id="element-2154" />
                      <XAxis type="number" allowDecimals={false} tick={{
                    fontSize: 12
                  }} data-id="element-2155" />
                      <YAxis dataKey="name" type="category" width={130} tick={{
                    fontSize: 12
                  }} data-id="element-2156" />
                      <Tooltip data-id="element-2157" />
                      <Bar dataKey="count" fill="#DC2626" radius={[0, 4, 4, 0]} cursor="pointer" onClick={(data: any) => {
                    const role = data?.name as string;
                    openDrilldown(`Declined: ${role}`, filteredCandidates.filter(c => c.status === 'Offer Declined' && c.role === role), `Candidates who declined an offer for ${role}.`);
                  }} data-id="element-2158" />
                    </BarChart>
                  </ResponsiveContainer>}
              </div>
            </div>

            {/* Salary Distribution */}
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 min-w-0 overflow-hidden" data-id="element-2159">
              <h3 className="text-lg font-semibold text-gray-900 mb-6" data-id="element-2160">
                Declined Offer Salary Distribution
              </h3>
              <div className="h-80 w-full" data-id="element-2161">
                {stats.offersDeclined === 0 ? <div className="h-full flex flex-col items-center justify-center text-gray-400" data-id="element-2162">
                    <DollarSign className="h-12 w-12 mb-3 opacity-20" data-id="element-2163" />
                    <p data-id="element-2164">No declined offers yet</p>
                  </div> : <ResponsiveContainer width="100%" height="100%" data-id="element-2165">
                    <BarChart data={declinedSalaryData} margin={{
                  top: 5,
                  right: 20,
                  left: 0,
                  bottom: 5
                }} data-id="element-2166">
                      <CartesianGrid strokeDasharray="3 3" vertical={false} data-id="element-2167" />
                      <XAxis dataKey="name" tick={{
                    fontSize: 12
                  }} interval={0} data-id="element-2168" />
                      <YAxis allowDecimals={false} tick={{
                    fontSize: 12
                  }} data-id="element-2169" />
                      <Tooltip data-id="element-2170" />
                      <Bar dataKey="count" fill="#EF4444" radius={[4, 4, 0, 0]} cursor="pointer" onClick={(data: any) => {
                    const bucket = data?.name as string;
                    const inBucket = (amt: number) => {
                      if (bucket === '<$50k') return amt < 50000;
                      if (bucket === '$50–60k') return amt >= 50000 && amt < 60000;
                      if (bucket === '$60–70k') return amt >= 60000 && amt < 70000;
                      if (bucket === '$70–80k') return amt >= 70000 && amt < 80000;
                      if (bucket === '$80–90k') return amt >= 80000 && amt < 90000;
                      return amt >= 90000;
                    };
                    openDrilldown(`Declined offers: ${bucket}`, filteredCandidates.filter(c => c.status === 'Offer Declined' && typeof c.offerAmount === 'number' && inBucket(c.offerAmount!)), `Candidates who declined offers in the ${bucket} range.`);
                  }} data-id="element-2171" />
                    </BarChart>
                  </ResponsiveContainer>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Drilldown modal — opens when any chart, KPI, or activity metric is clicked */}
      <DrilldownModal isOpen={drilldown !== null} onClose={() => setDrilldown(null)} title={drilldown?.title || ''} subtitle={drilldown?.subtitle} candidates={drilldown?.candidates || []} data-id="element-2172" />
    </div>;
}